import React from 'react';
import PropTypes from 'prop-types';
import classNames from 'classnames';
import { connect } from 'react-redux';

// Utils
import { closePopup } from '../../../../components/popupPanel/popupActions';
import { analyticsEvent } from '../../../../analytics';
import quickNotesPopupStayActivePredicate from './popup/quickNotesPopupStayActivePredicate';
import quickNotesCanDrop from './popup/quickNotesCanDrop';
import { inboxDropResultFn } from '../../../../element/dnd/dropResultFn';
import { getElementId } from '../../../../../common/elements/utils/elementPropertyUtils';
import { isCommentThread, isContent } from '../../../../../common/elements/utils/elementTypeUtils';
import preloadImages from '../../../../utils/preloadImages';

// Selectors
import { quickNotesUtilSelector } from './quickNotesSelector';

// Actions
import * as boardService from '../../../../element/board/boardService';

// Decorators
import ElementDropTarget from '../../../../element/dnd/elementDropTargets/ElementDropTarget';
import elementShortcutsDecorator from '../../../shortcuts/clipboard/elementShortcutsDecorator';

// Components
import PopupTrigger from '../../../../components/popupPanel/PopupTrigger';
import QuickNotesPopup from './popup/QuickNotesPopup';
import Icon from '../../../../components/icons/Icon';
import TooltipSource from '../../../../components/tooltips/TooltipSource';
import QuickNotesCount from './QuickNotesCount';
import QuickNotesIndicator from './QuickNotesIndicator';

// Constants
import { DROP_TARGET_TYPES, ELEMENT_DND_TYPE } from '../../../../../common/elements/elementConstants';
import { PopupIds, PopupSections } from '../../../../components/popupPanel/popupConstants';
import { NATIVE_TYPES } from '../../../../utils/dnd/dragAndDropUtils';

// Styles
import './QuickNotesUtil.scss';
import { quickNotesElementSelector } from './quickNotesElementsSelector';

const mapDispatchToProps = (dispatch) => ({
    onPopupToggle: (isPopupOpen) => {
        isPopupOpen && analyticsEvent('viewed-quick-notes');
    },
    closePopupPanel: () => dispatch(closePopup(PopupIds.QUICK_NOTES)),
    fetchBoard: (boardId) =>
        dispatch(
            boardService.fetchBoard({
                boardId,
                excludeSelf: false,
                loadAncestors: false,
            }),
        ),
});

const getQuickNotesDropLocation = (props, monitor, domNode) => {
    const dropResult = inboxDropResultFn(props, monitor, domNode);

    const itemType = monitor.getItemType();

    if (itemType !== ELEMENT_DND_TYPE) return { ...dropResult };

    // Can't drop lines onto the inbox
    const { draggedElements } = monitor.getItem();
    const acceptedElementIds = draggedElements.filter((el) => isContent(el) || isCommentThread(el)).map(getElementId);

    return {
        ...dropResult,
        acceptedElementIds,
    };
};

const dropTargetConfig = {
    drop: getQuickNotesDropLocation,
    canDrop: quickNotesCanDrop,
    acceptDropTypes: NATIVE_TYPES,
    hoverType: DROP_TARGET_TYPES.INBOX_CLOSED,
};

@connect(quickNotesElementSelector)
@connect(quickNotesUtilSelector, mapDispatchToProps)
@elementShortcutsDecorator
@ElementDropTarget(dropTargetConfig)
class QuickNotesUtil extends React.Component {
    componentDidMount() {
        const { quickNotesRootId, fetchBoard } = this.props;
        if (!quickNotesRootId) return;

        fetchBoard(quickNotesRootId);

        preloadImages([
            require('./popup/images/quick-notes-empty-phone-dark.png'),
            require('./popup/images/quick-notes-empty-phone-light.png'),
            require('./popup/images/quick-notes-empty-qr-code.png'),
        ]);
    }

    componentDidUpdate(prevProps) {
        const { closePopupPanel, quickNotesChildIds } = this.props;

        if (prevProps.quickNotesChildIds.length > 0 && quickNotesChildIds.length === 0) {
            closePopupPanel();
        }
    }

    render() {
        const {
            onPopupToggle,
            quickNotesRootId,
            quickNotesChildIds,
            connectDropTarget,
            canDrop,
            isHovered,
            tooltipEnabled,
            query,
            newQuickNotesCount,
        } = this.props;

        if (!quickNotesRootId) return null;

        return (
            <div className={classNames('HeaderUtil QuickNotesUtil', { 'can-drop': canDrop, hovered: isHovered })}>
                <TooltipSource
                    tooltipText="Quick notes"
                    distance={0}
                    offset={4}
                    delay={700}
                    enabled={tooltipEnabled}
                    triggerOnMouseEnter
                >
                    <div ref={connectDropTarget} className="drop-target-wrapper">
                        <PopupTrigger
                            popupId={PopupIds.QUICK_NOTES}
                            className="button-quick-notes header-util-button"
                            stayActivePredicate={quickNotesPopupStayActivePredicate}
                            closeSection={PopupSections.HEADER}
                            onToggle={onPopupToggle}
                        >
                            {(popupOpen) => (
                                <div className="header-util-button-content">
                                    <Icon name="nav-quick-notes" />
                                    <QuickNotesIndicator count={newQuickNotesCount} hide={popupOpen} />
                                    <QuickNotesCount query={query} count={quickNotesChildIds.length} />
                                </div>
                            )}
                        </PopupTrigger>
                    </div>
                </TooltipSource>
                <QuickNotesPopup {...this.props} />
            </div>
        );
    }
}

QuickNotesUtil.propTypes = {
    tooltipEnabled: PropTypes.bool,
    popupOpen: PropTypes.bool,
    canDrop: PropTypes.bool,
    isHovered: PropTypes.bool,
    quickNotesRootId: PropTypes.string,
    quickNotesChildIds: PropTypes.array,
    onPopupToggle: PropTypes.func,
    closePopupPanel: PropTypes.func,
    fetchBoard: PropTypes.func,
    connectDropTarget: PropTypes.func,
    query: PropTypes.string,
    newQuickNotesCount: PropTypes.number,
};

export default QuickNotesUtil;
